class OfferItems extends HTMLElement {
  constructor() {
    super();
    this.heroRequestId = 0;
    // Independent generation token for the hero MEDIA (gallery + modal). In-place
    // bundles keep the page hero's text but can swap only the gallery to the
    // bundle's own image, so media is tracked separately from currentHeroKey.
    this.mediaRequestId = 0;
    // Tracks in-flight hover prefetches so they can be aborted (pointerleave,
    // reinit, disconnect) instead of running to completion and being thrown away.
    this.prefetchControllers = new Set();
    this.addEventListener('change', this.onChange.bind(this));
  }

  disconnectedCallback() {
    this.abortPrefetches();
  }

  abortPrefetches() {
    this.prefetchControllers.forEach(controller => controller.abort());
    this.prefetchControllers.clear();
  }

  connectedCallback() {
    this.formId = this.dataset.productFormId;
    this.sectionId = this.dataset.sectionId;
    // On first render the DOM shows exactly the server-rendered product/variant.
    this.currentHeroKey = this.dataset.currentHero || null;
    // The media channel starts on the same base product/variant as the hero.
    this.currentMediaKey = this.dataset.currentHero || null;
    // Snapshot the base (1x) price markup so an in-place bundle selection can be
    // undone by simply restoring it when the 1x card is picked again – captured
    // before applySelection so the default 1x card never overrides it with itself.
    this.captureBasePrice();
    // The product title is product-level (same across variants), so snapshot it
    // once here to restore it when switching back to the 1x card. Not re-captured
    // in reinit() to avoid snapshotting a still-shown cross-product title.
    this.captureBaseTitle();
    this.applySelection();
  }

  // Re-applies the offer selection + hero after the inner markup was (re)mounted.
  applySelection() {
    this.syncSelection();
    this.bindPrefetch();
    this.handleSelection(this.selectedRadio);
  }

  // Called after the section is re-rendered (e.g. on variant switch),
  // since the inner markup is replaced but the element instance persists.
  reinit() {
    // Invalidate any in-flight hero swap from before this re-render. Also clear
    // the loading state: the superseded request's finally() won't (its id is now
    // stale), and the follow-up updateHero() may early-return without re-adding
    // it – otherwise the progress cursor could stick permanently.
    this.heroRequestId++;
    // Also invalidate any in-flight in-place bundle media swap from the previous
    // variant so it can't land on the new variant's gallery.
    this.mediaRequestId++;
    this.classList.remove('offer-items--loading');
    // The inner markup (and its radios) is about to be replaced – cancel any
    // prefetches still running on the now-detached old radios.
    this.abortPrefetches();

    // product-selector has already patched #Product-Price to the freshly picked
    // variant's unit (1x) price – re-snapshot it as the new base so 1x restores
    // the right price after a variant switch.
    this.captureBasePrice();

    const baseKey = this.dataset.currentHero || null;
    const baseProduct = baseKey ? baseKey.split('|')[0] : null;
    const shownProduct = this.currentHeroKey
      ? this.currentHeroKey.split('|')[0]
      : null;

    // If the hero still shows the SAME product (just a different variant), sync
    // to the new base key so no needless refetch happens – product-selector
    // already patched price/offers and handles the variant's media.
    // If it shows a DIFFERENT product (a previously selected cross-product
    // offer), keep the now-stale key so updateHero() re-renders the FULL base
    // hero, since renderProductInfo only patched price + offers (not title,
    // media, description or checkmarks).
    if (shownProduct === baseProduct) {
      this.currentHeroKey = baseKey;
    }

    this.applySelection();
  }

  onChange(event) {
    if (!event.target.matches('[data-offer-radio]')) return;
    this.syncSelection();
    this.handleSelection(this.selectedRadio);
  }

  // Decides how a selection updates the hero:
  // - no card        -> restore base hero (variant has no offers anymore)
  // - inline card    -> 1x base card or in-place native bundle: no fetch, keep the
  //                     page hero and only set the price locally. Recognised by
  //                     same product OR an injected price (data-offer-price).
  // - other product  -> cross-product: full hero swap via Section Rendering
  handleSelection(radio) {
    if (!radio) {
      // An in-place bundle rewrites the base hero's title/price LOCALLY (no
      // fetch), so currentHeroKey still points at the base product. When the new
      // variant has no offer cards, resetHeroToBase() would early-return on the
      // matching key and leave that stale bundle title/price behind. Restore the
      // base snapshot locally first; if a cross-product hero is shown instead,
      // resetHeroToBase() re-fetches the full base hero and overrides this.
      this.restoreHeroToBaseLocal();
      this.resetHeroToBase();
      return;
    }
    if (this.isSameProduct(radio) || radio.dataset.offerPrice) {
      this.applyInlinePrice(radio);
    } else {
      this.updateHero(radio);
    }
  }

  // The product URL the PDP is actually for (from data-current-hero).
  get baseProductUrl() {
    const key = this.dataset.currentHero || '';
    const sep = key.indexOf('|');
    return sep === -1 ? key : key.slice(0, sep);
  }

  // The 1x base card points at the page product itself (offer_product = product).
  isSameProduct(radio) {
    return !!radio && radio.dataset.offerUrl === this.baseProductUrl;
  }

  // Inline selection (1x base card or in-place native bundle): never fetch. If a
  // cross-product hero is currently shown, restore the base hero first, THEN apply
  // this card's price (otherwise the price would land on the wrong product's hero).
  applyInlinePrice(radio) {
    const shownProduct = this.currentHeroKey
      ? this.currentHeroKey.split('|')[0]
      : null;

    if (shownProduct !== this.baseProductUrl) {
      // Apply the price for whatever card is selected WHEN the reset lands, not
      // the one captured here: if the user switches cards again while the base
      // hero fetch is in flight (it runs synchronous setHeroPrice on the newer
      // card without bumping heroRequestId), a stale afterRender would otherwise
      // reapply this card's price on top of the newer selection – leaving the
      // shown price/title out of sync with the checked card + submitted variant.
      this.resetHeroToBase(() => {
        const current = this.selectedRadio;
        if (current) {
          this.setHeroPrice(current);
          this.applyInlineMedia(current);
        }
      });
    } else {
      this.setHeroPrice(radio);
      this.applyInlineMedia(radio);
    }
  }

  // Gallery side of an inline selection: swap to the bundle's own image when the
  // card carries one (data-offer-hero-media), otherwise restore the base gallery.
  // Kept separate from the price/title swap so the page product's hero text stays.
  applyInlineMedia(radio) {
    if (radio && radio.dataset.offerHeroMedia) {
      this.swapHeroMedia(
        radio.dataset.offerUrl,
        radio.value,
        this.heroKeyOf(radio)
      );
    } else {
      this.restoreBaseMedia();
    }
  }

  // In-place bundle cards carry a formatted bundle price/compare in data
  // attributes; the 1x card carries none, so it restores the snapshotted base
  // price markup.
  //
  // We ALWAYS rebuild from the base markup first, then swap ONLY the price value
  // node inside .price__container, plus the "Inhalt:" unit-price line and the
  // title for bundles. The "inkl. MwSt"/shipping notice stays as on the 1x price.
  setHeroPrice(radio) {
    // Title swap is independent of the price block (different element), so do it
    // first and unconditionally – also restores the base title for the 1x card.
    this.setHeroTitle(radio);

    const priceEl = this.sectionRoot.querySelector(
      `#Product-Price-${this.sectionId}`
    );
    if (!priceEl) return;
    if (this.basePriceHtml == null) return;

    // Reset to the full base price block (price + notices + "Inhalt").
    priceEl.innerHTML = this.basePriceHtml;

    const offerPrice = radio.dataset.offerPrice;
    if (!offerPrice) return; // 1x → base price already restored, nothing to swap.

    // Swap the Grundpreis ("Inhalt: X ml (Y €/1 l)") to the bundle variant's own
    // unit price. If the bundle has none, hide the line so the restored 1x unit
    // price isn't shown next to the (different) bundle price.
    const unitEl = priceEl.querySelector('[data-product-unit-price]');
    if (unitEl) {
      const unit = radio.dataset.offerUnitprice;
      if (unit) {
        unitEl.textContent = unit;
        unitEl.hidden = false;
      } else {
        unitEl.hidden = true;
      }
    }

    const container = priceEl.querySelector('.price__container');
    if (!container) return;

    // The price value is the first .price__regular / .price__sale child; the tax
    // notice + unit price are later siblings we must leave untouched.
    const valueNode = container.querySelector(
      ':scope > .price__regular, :scope > .price__sale'
    );
    const compare = radio.dataset.offerCompare;
    const onSale = !!compare && compare !== offerPrice;

    // Price first, struck original small and behind it (per design).
    const saleHtml = onSale
      ? `<div class="price__sale"><div class="price__sale-inner"><ins>${offerPrice}</ins><del class="offer-hero__compare">${compare}</del></div></div>`
      : `<div class="price__regular"><span>${offerPrice}</span></div>`;

    if (valueNode) {
      valueNode.outerHTML = saleHtml;
    } else {
      container.insertAdjacentHTML('afterbegin', saleHtml);
    }

    const priceWrapper = priceEl.querySelector('.price');
    if (priceWrapper) priceWrapper.classList.toggle('price--on-sale', onSale);
  }

  captureBasePrice() {
    const priceEl = this.sectionRoot.querySelector(
      `#Product-Price-${this.sectionId}`
    );
    if (priceEl) this.basePriceHtml = priceEl.innerHTML;
  }

  captureBaseTitle() {
    const titleEl = this.sectionRoot.querySelector('.product__title');
    if (titleEl) this.baseTitleHtml = titleEl.innerHTML;
  }

  // Swaps the hero title to the selected card's title (e.g. "5x Laundry Perfume
  // Drops") for in-place bundles, and restores the base product title for the 1x
  // card. Cross-product cards swap the title via the full hero fetch instead.
  setHeroTitle(radio) {
    const titleEl = this.sectionRoot.querySelector('.product__title');
    if (!titleEl || this.baseTitleHtml == null) return;

    const offerTitle = radio.dataset.offerTitle;
    if (offerTitle) {
      titleEl.textContent = offerTitle;
    } else {
      titleEl.innerHTML = this.baseTitleHtml;
    }
  }

  // Restores the base product's title + price markup from the snapshots without a
  // fetch. Used when there is no offer card to derive the hero from (e.g. the
  // picked variant has no offers) and the hero key already matches the base, so
  // resetHeroToBase() would early-return and leave a previously injected in-place
  // bundle title/price on the page.
  restoreHeroToBaseLocal() {
    const titleEl = this.sectionRoot.querySelector('.product__title');
    if (titleEl && this.baseTitleHtml != null) {
      titleEl.innerHTML = this.baseTitleHtml;
    }

    const priceEl = this.sectionRoot.querySelector(
      `#Product-Price-${this.sectionId}`
    );
    if (priceEl && this.basePriceHtml != null) {
      priceEl.innerHTML = this.basePriceHtml;
    }

    // An in-place bundle may also have swapped the gallery; restore it too, since
    // resetHeroToBase() early-returns here (the hero key already equals the base).
    this.restoreBaseMedia();
  }

  get selectedRadio() {
    return (
      this.querySelector('[data-offer-radio]:checked') ||
      this.querySelector('[data-offer-radio]:not(:disabled)')
    );
  }

  syncSelection() {
    const radio = this.selectedRadio;
    if (!radio) return;

    if (!radio.checked) radio.checked = true;

    this.querySelectorAll('[data-offer-item]').forEach(item => {
      item.classList.toggle('is-selected', item.contains(radio));
    });

    this.setFormVariant(radio.value);
    this.updateAddButton(radio);
    this.updateQuantityCap(radio);
  }

  // Mirrors product-selector's quantity logic for the picked offer variant.
  // No-ops when there is no quantity field (current layout); future-proofs the
  // case where a free quantity input sits next to the offer cards so its "max"
  // tracks the selected offer's stock instead of the last variant-picker state.
  updateQuantityCap(radio) {
    const quantityInput = document.querySelector(
      '.product-selector__quantity quantity-input input'
    );
    if (!quantityInput) return;

    const managed = radio.dataset.inventoryManaged === 'true';
    const available = radio.dataset.available === 'true';
    const quantity = parseInt(radio.dataset.inventoryQuantity, 10);

    // Not tracked, or "continue selling when sold out" (managed but 0 in stock
    // yet still available) → no hard cap.
    const sellsWhenSoldOut =
      managed && available && (Number.isNaN(quantity) || quantity <= 0);

    if (!managed || sellsWhenSoldOut) {
      quantityInput.setAttribute('max', '');
      return;
    }

    quantityInput.setAttribute('max', quantity);
    if (Number(quantityInput.value) > quantity) {
      quantityInput.value = quantity;
    }
  }

  // Reflects the selected offer's availability on the add-to-cart button.
  // Without this the button keeps the base picker variant's state, so an
  // available offer could still show "sold out" (or vice versa).
  updateAddButton(radio) {
    const form = document.getElementById(`ProductForm-${this.sectionId}`);
    if (!form || typeof form.toggleAddButton !== 'function') return;

    // Sold-out offers stay selectable; the add button just flips to the shop's
    // standard "Sold out" state (mirrors normal variant behaviour).
    if (radio.dataset.available === 'false') {
      const soldOut =
        (window.variantStrings && window.variantStrings.soldOut) || '';
      form.toggleAddButton(true, soldOut);
    } else {
      form.toggleAddButton(false);
    }
  }

  setFormVariant(variantId) {
    if (!variantId) return;

    const inputs = new Set();
    document
      .querySelectorAll(`input[name="id"][form="${this.formId}"]`)
      .forEach(input => inputs.add(input));

    const form = document.getElementById(this.formId);
    if (form) {
      form
        .querySelectorAll('input[name="id"]')
        .forEach(input => inputs.add(input));
    }

    // Set the value directly without dispatching a bubbling "change":
    // the [name="id"] input lives inside <product-selector>, whose change
    // handler would otherwise reset the id back to the base variant.
    inputs.forEach(input => {
      if (input.value === variantId) return;
      input.value = variantId;
    });

    // Keep the separate installments/payment-terms form in sync as well.
    // It lives OUTSIDE <product-selector>, so here we DO dispatch "change"
    // (like product-selector's updateVariantInput) so Shop Pay / payment terms
    // re-fetch the financing estimate for the selected offer variant.
    const installments = document.getElementById(
      `Product-Installments-${this.sectionId}`
    );
    if (installments) {
      installments
        .querySelectorAll('input[name="id"]')
        .forEach(input => {
          if (input.value === variantId) return;
          input.value = variantId;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
    }
  }

  buildFetchUrl(url, variantId) {
    return `${url}?variant=${encodeURIComponent(
      variantId
    )}&section_id=${encodeURIComponent(this.sectionId)}`;
  }

  heroKeyOf(radio) {
    return `${radio.dataset.offerUrl}|${radio.value}`;
  }

  // Prefetch on hover / pointerdown so the section HTML is already on its way
  // by the time the card is actually selected. The prefetch is tied to THIS
  // imminent interaction: it is consumed on selection and discarded when the
  // pointer leaves – so nothing stale is ever reused across switches.
  bindPrefetch() {
    // Safe to call on every applySelection() (connectedCallback AND reinit):
    // reinit replaces all children, so each call only binds listeners on fresh
    // nodes – no duplicate listeners accumulate on the same element.
    this.querySelectorAll('[data-offer-radio]').forEach(radio => {
      const item = radio.closest('[data-offer-item]') || radio;
      const start = () => this.prefetch(radio);
      const clear = () => {
        if (radio._heroPrefetch) {
          // Cancel the in-flight fetch so leaving a card doesn't leave a wasted
          // section render (+ DOMParser + image preload) running in the background.
          radio._heroPrefetch.controller.abort();
          radio._heroPrefetch = null;
        }
      };
      item.addEventListener('pointerenter', start);
      item.addEventListener('pointerdown', start);
      item.addEventListener('pointerleave', clear);
    });
  }

  prefetch(radio) {
    if (!radio || radio.disabled) return;
    // Inline cards (1x / volume / in-place bundle) never swap the hero, so there
    // is nothing to prefetch – and we must not warm the bundle's bare PDP.
    if (radio.dataset.offerPrice) return;

    const url = radio.dataset.offerUrl;
    const variantId = radio.value;
    if (!url) return;

    const key = this.heroKeyOf(radio);
    // Already displayed, or a prefetch for this exact key is already running.
    if (key === this.currentHeroKey) return;
    if (radio._heroPrefetch && radio._heroPrefetch.key === key) return;

    const controller = new AbortController();
    this.prefetchControllers.add(controller);

    const promise = fetch(this.buildFetchUrl(url, variantId), {
      signal: controller.signal,
    })
      .then(response => {
        // Reject non-OK prefetches so a later click falls into renderHero's
        // .catch instead of swapping an error page into the PDP.
        if (!response.ok) {
          throw new Error(`Hero prefetch failed: ${response.status}`);
        }
        return response.text();
      })
      .then(text =>
        new DOMParser().parseFromString(text, 'text/html')
      );

    // Warm the gallery image so it paints instantly on switch, and drop the
    // controller from the registry once the request settles (or is aborted).
    promise
      .then(doc => this.preloadImage(doc))
      .catch(() => {})
      .finally(() => this.prefetchControllers.delete(controller));

    radio._heroPrefetch = { key, promise, controller };
  }

  preloadImage(doc) {
    const img = doc.querySelector('.product__media img');
    if (!img) return;
    const src = img.getAttribute('src') || img.getAttribute('data-src');
    if (src) {
      const preload = new Image();
      preload.src = src;
    }
  }

  // Re-renders the main product hero (gallery, title, subtitle, price) for the
  // selected offer's product using the Section Rendering API. The offer-items
  // block itself is intentionally NOT replaced, so the selection persists.
  updateHero(radio) {
    if (!radio) return;

    const url = radio.dataset.offerUrl;
    const variantId = radio.value;
    if (!url) return;

    this.renderHero(url, variantId, this.heroKeyOf(radio), radio);
  }

  // Resets the hero back to the base product/variant (the product the PDP is
  // actually for). Used when the current variant has no offer cards but the
  // hero still shows a previously selected cross-product offer.
  resetHeroToBase(afterRender) {
    const baseKey = this.dataset.currentHero;
    if (!baseKey) return;

    const sep = baseKey.indexOf('|');
    if (sep === -1) return;
    const url = baseKey.slice(0, sep);
    const variantId = baseKey.slice(sep + 1);
    if (!url) return;

    this.renderHero(url, variantId, baseKey, null, afterRender);
  }

  // Shared hero renderer. prefetchRadio is optional – when given, its in-flight
  // hover prefetch is consumed instead of starting a fresh fetch. afterRender is
  // an optional callback run once the swap lands (used to set a volume price on
  // top of a freshly restored base hero).
  renderHero(url, variantId, heroKey, prefetchRadio, afterRender) {
    if (!url) return;
    if (heroKey === this.currentHeroKey) return;
    this.currentHeroKey = heroKey;

    // Generation token: only the most recent request may touch the DOM, so a
    // slower earlier fetch (rapid clicks or a variant-driven reinit) can never
    // overwrite a newer selection.
    const requestId = ++this.heroRequestId;
    // A full swap also replaces the gallery, so invalidate any in-flight in-place
    // bundle media swap – otherwise a slower bundle media fetch could land on top.
    this.mediaRequestId++;
    const sectionId = this.sectionId;

    // Use the in-flight prefetch for this card if available, then consume it.
    let docPromise;
    if (
      prefetchRadio &&
      prefetchRadio._heroPrefetch &&
      prefetchRadio._heroPrefetch.key === heroKey
    ) {
      docPromise = prefetchRadio._heroPrefetch.promise;
      prefetchRadio._heroPrefetch = null;
    } else {
      docPromise = fetch(this.buildFetchUrl(url, variantId))
        .then(response => {
          // Don't inject an error page (404/500/deleted offer product) into the
          // live PDP – fail the chain so the .catch leaves the current hero.
          if (!response.ok) {
            throw new Error(`Hero fetch failed: ${response.status}`);
          }
          return response.text();
        })
        .then(text =>
          new DOMParser().parseFromString(text, 'text/html')
        );
    }

    this.classList.add('offer-items--loading');

    docPromise
      .then(html => {
        // A newer selection (or a reinit) has superseded this request.
        if (requestId !== this.heroRequestId) return;

        this.replaceTitle(html);
        this.replaceSubtitle(html);
        this.replaceInner(html, `#Product-Price-${sectionId}`);
        this.replaceInner(html, `#Product-Description-${sectionId}`);
        this.replaceInner(html, `#Product-Checkmarks-${sectionId}`);
        this.replaceOuter(html, '.product__media');
        this.replaceOuter(html, `#ProductModal-${sectionId}`);
        // A full hero swap also replaced the gallery, so keep the media channel
        // key in sync (otherwise a later in-place bundle media swap / restore
        // would mis-detect what the gallery currently shows).
        this.currentMediaKey = heroKey;
        this.initMedia();

        // Base hero restored – let the caller layer a volume price on top.
        if (typeof afterRender === 'function') afterRender();
      })
      .catch(() => {
        // Only the latest request may reset state. We mark the hero key as
        // UNKNOWN (null) rather than restoring previousHeroKey: with overlapping
        // fetches previousHeroKey can point at a hero that never actually
        // rendered (e.g. A→B clicked fast, B fails while A is still in flight and
        // gets discarded). null guarantees the next click on ANY card differs
        // from currentHeroKey and triggers a clean refetch instead of the
        // `heroKey === currentHeroKey` early-return getting stuck.
        if (requestId === this.heroRequestId) {
          this.currentHeroKey = null;
        }
      })
      .finally(() => {
        if (requestId === this.heroRequestId) {
          this.classList.remove('offer-items--loading');
        }
      });
  }

  // Swaps ONLY the hero gallery + modal to the given product/variant's media
  // (via Section Rendering), keeping the page product's title/price/description.
  // Used both for an in-place bundle's own image and to restore the base gallery.
  // Tracked on its own generation token + key (currentMediaKey/mediaRequestId) so
  // it never collides with the full-hero channel used by cross-product offers.
  // NOTE: needs the product reachable by URL (the page product always is; a hidden
  // bundle product must be published to the Online Store, even if hidden from
  // search/collections) – a failed fetch just leaves the current gallery in place.
  swapHeroMedia(url, variantId, mediaKey) {
    if (!url) return;
    if (mediaKey === this.currentMediaKey) return;
    this.currentMediaKey = mediaKey;

    const requestId = ++this.mediaRequestId;
    this.classList.add('offer-items--loading');

    fetch(this.buildFetchUrl(url, variantId))
      .then(response => {
        if (!response.ok) {
          throw new Error(`Hero media fetch failed: ${response.status}`);
        }
        return response.text();
      })
      .then(text => new DOMParser().parseFromString(text, 'text/html'))
      .then(html => {
        // A newer media swap (or restore) has superseded this request.
        if (requestId !== this.mediaRequestId) return;
        this.replaceOuter(html, '.product__media');
        this.replaceOuter(html, `#ProductModal-${this.sectionId}`);
        this.initMedia();
      })
      .catch(() => {
        // Leave the current gallery untouched; mark the media key UNKNOWN so the
        // next selection always re-evaluates instead of early-returning on a key
        // that never actually rendered.
        if (requestId === this.mediaRequestId) {
          this.currentMediaKey = null;
        }
      })
      .finally(() => {
        if (requestId === this.mediaRequestId) {
          this.classList.remove('offer-items--loading');
        }
      });
  }

  // Restores the page product's gallery (at the currently picked variant) after an
  // in-place bundle had swapped it. Re-fetches rather than caching so the markup
  // is clean (pre-Swiper-init) and the [data-selected] slide matches the CURRENT
  // variant, not the one shown on first load.
  restoreBaseMedia() {
    const baseKey = this.dataset.currentHero || null;
    if (!baseKey) return;
    const sep = baseKey.indexOf('|');
    if (sep === -1) return;
    this.swapHeroMedia(baseKey.slice(0, sep), baseKey.slice(sep + 1), baseKey);
  }

  // Re-initialises the (re)mounted <product-media> gallery: its connectedCallback
  // should do this, but call it explicitly as a safety net (init is idempotent),
  // then jump to the variant's [data-selected] slide so the shown image matches.
  initMedia() {
    const media = this.sectionRoot.querySelector('product-media');
    if (!media || typeof media.init !== 'function') return;

    if (!(media.settings && media.settings.sliderInstance)) {
      media.init();
    }

    const slider = media.settings && media.settings.sliderInstance;
    const selected = media.querySelector('.swiper-slide[data-selected]');
    const idx = Number(selected && selected.dataset.index);
    if (slider && !Number.isNaN(idx) && slider.activeIndex !== idx) {
      slider.slideTo(idx, 0);
    }
  }

  // Scope all hero replacements to THIS section's wrapper. Class selectors like
  // .product__title / .product__media are not unique on the page (quick-view,
  // featured-product, "recently viewed" sliders reuse them), so an unscoped
  // document.querySelector could patch the wrong block.
  get sectionRoot() {
    return (
      document.getElementById(`shopify-section-${this.sectionId}`) ||
      this.closest('.shopify-section') ||
      document
    );
  }

  replaceInner(html, selector) {
    const destination = this.sectionRoot.querySelector(selector);
    if (!destination) return;
    const source = html.querySelector(selector);
    // Clear the target when the new product's section omits this region (e.g. an
    // offer product without a description/checkmarks block) so the previous
    // product's text can't stay visible under the new hero.
    destination.innerHTML = source ? source.innerHTML : '';
  }

  // The title must NEVER be wiped: unlike description/checkmarks, an empty title
  // looks broken. If the fetched section omits .product__title (gift-card
  // template, a different template suffix, or the title block switched off),
  // keep the current title instead of clearing it.
  replaceTitle(html) {
    const destination = this.sectionRoot.querySelector('.product__title');
    if (!destination) return;
    const source = html.querySelector('.product__title');
    if (!source) return;
    destination.innerHTML = source.innerHTML;
  }

  // The subtitle (category text) may be absent on some products – clear it then.
  replaceSubtitle(html) {
    const destination = this.sectionRoot.querySelector(
      '.product__text.kategorie'
    );
    if (!destination) return;
    const source = html.querySelector('.product__text.kategorie');
    destination.innerHTML = source ? source.innerHTML : '';
  }

  // Replaces the whole element so custom elements (e.g. <product-media>) run
  // their connectedCallback again and re-initialise (Swiper gallery, modal).
  replaceOuter(html, selector) {
    const destination = this.sectionRoot.querySelector(selector);
    const source = html.querySelector(selector);
    if (destination && source) {
      destination.outerHTML = source.outerHTML;
    }
  }
}

customElements.define('offer-items', OfferItems);
